import React, { useEffect, useState, useCallback } from 'react';
import { modemApi } from '../../services/api';
import { useWebSocket } from '../../hooks/useWebSocket';
import toast from 'react-hot-toast';
import './Modem.css';

type Tab = 'status' | 'sms' | 'wifi' | 'config';

interface ModemStatus {
  connected: boolean;
  ip: string | null;
  operator: string;
  network_type: string;
  signal_percent: number;
  signal_dbm: number | null;
  rx_bytes: number;
  tx_bytes: number;
}

interface SMS {
  index: number;
  status: string;
  sender: string;
  timestamp: string;
  body: string;
}

interface WiFiNet {
  ssid: string;
  signal_percent: number;
  signal_dbm: number;
  encrypted: boolean;
}

function fmtBytes(b: number) {
  if (b > 1e9) return (b / 1e9).toFixed(2) + ' GB';
  if (b > 1e6) return (b / 1e6).toFixed(1) + ' MB';
  if (b > 1e3) return (b / 1e3).toFixed(0) + ' KB';
  return b + ' B';
}

function SignalBar({ percent }: { percent: number }) {
  const bars = 5;
  const filled = Math.round((percent / 100) * bars);
  const color = percent > 60 ? '#00ff88' : percent > 30 ? '#ffaa00' : '#ff4444';
  return (
    <span className="signal-bars">
      {Array.from({ length: bars }, (_, i) => (
        <span key={i} className="signal-bar" style={{ background: i < filled ? color : '#2a3a4a' }} />
      ))}
      <span style={{ color, marginLeft: 6 }}>{percent}%</span>
    </span>
  );
}

export default function Modem() {
  const [tab, setTab] = useState<Tab>('status');
  const [status, setStatus] = useState<ModemStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [smsList, setSmsList] = useState<SMS[]>([]);
  const [smsTo, setSmsTo] = useState('');
  const [smsBody, setSmsBody] = useState('');
  const [wifiNets, setWifiNets] = useState<WiFiNet[]>([]);
  const [wifiStatus, setWifiStatus] = useState<{ connected: boolean; ssid: string | null; ip: string | null } | null>(null);
  const [wifiPassword, setWifiPassword] = useState<Record<string, string>>({});
  const [apn, setApn] = useState('');
  const [apnUser, setApnUser] = useState('');
  const [apnPass, setApnPass] = useState('');
  const [loading, setLoading] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const r = await modemApi.status();
      setStatus(r.data);
    } catch {
      toast.error('Modem API əlçatmaz');
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const fetchSms = useCallback(async () => {
    try {
      const r = await modemApi.smsList();
      setSmsList([...r.data].reverse());
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'SMS oxunmadı');
    }
  }, []);

  const fetchWifi = useCallback(async () => {
    setLoading('wifi-scan');
    try {
      const [scan, stat] = await Promise.all([modemApi.wifiScan(), modemApi.wifiStatus()]);
      setWifiNets(scan.data);
      setWifiStatus(stat.data);
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'WiFi skan xətası');
    } finally {
      setLoading(null);
    }
  }, []);

  const fetchApn = useCallback(async () => {
    try {
      const r = await modemApi.getApn();
      setApn(r.data.apn || '');
      setApnUser(r.data.apn_user || '');
      setApnPass(r.data.apn_password || '');
    } catch {}
  }, []);

  const onWsMessage = useCallback((data: any) => {
    if (data.type === 'modem_update') {
      setStatus(prev => prev ? { ...prev, ...data.payload } : data.payload);
    }
  }, []);

  useWebSocket(onWsMessage);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);
  useEffect(() => { if (tab === 'sms') fetchSms(); }, [tab, fetchSms]);
  useEffect(() => { if (tab === 'wifi') fetchWifi(); }, [tab, fetchWifi]);
  useEffect(() => { if (tab === 'config') fetchApn(); }, [tab, fetchApn]);

  const handleConnect = async () => {
    setLoading('connect');
    try {
      await modemApi.connect();
      toast.success('4G bağlantısı quruldu');
      fetchStatus();
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Bağlantı xətası');
    } finally {
      setLoading(null);
    }
  };

  const handleDisconnect = async () => {
    setLoading('disconnect');
    try {
      await modemApi.disconnect();
      toast.success('4G kəsildi');
      fetchStatus();
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Xəta');
    } finally {
      setLoading(null);
    }
  };

  const handleSendSms = async () => {
    if (!smsTo || !smsBody) return;
    setLoading('sms-send');
    try {
      await modemApi.smsSend(smsTo, smsBody);
      toast.success(`SMS göndərildi: ${smsTo}`);
      setSmsTo('');
      setSmsBody('');
      fetchSms();
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'SMS göndərilmədi');
    } finally {
      setLoading(null);
    }
  };

  const handleDeleteSms = async (index: number) => {
    try {
      await modemApi.smsDelete(index);
      setSmsList(prev => prev.filter(s => s.index !== index));
    } catch {
      toast.error('SMS silinmədi');
    }
  };

  const handleWifiConnect = async (ssid: string) => {
    setLoading(`wifi-${ssid}`);
    try {
      await modemApi.wifiConnect(ssid, wifiPassword[ssid] || '');
      toast.success(`WiFi qoşulur: ${ssid}`);
      setTimeout(() => { fetchWifi(); setLoading(null); }, 5000);
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'WiFi bağlantı xətası');
      setLoading(null);
    }
  };

  const handleWifiDisconnect = async () => {
    try {
      await modemApi.wifiDisconnect();
      toast.success('WiFi kəsildi');
      fetchWifi();
    } catch {
      toast.error('Xəta');
    }
  };

  const handleSaveApn = async () => {
    if (!apn) return;
    setLoading('apn');
    try {
      await modemApi.setApn(apn, apnUser, apnPass);
      toast.success('APN saxlanıldı');
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'APN xətası');
    } finally {
      setLoading(null);
    }
  };

  const TABS: [Tab, string][] = [['status', 'STATUS'], ['sms', 'SMS'], ['wifi', 'WiFi'], ['config', 'KONFİQ']];

  return (
    <div className="modem-page">
      <div className="modem-header">
        <h2>SIM7670 MODEM</h2>
        {status && (
          <span className={`modem-badge ${status.connected ? 'online' : 'offline'}`}>
            {status.connected ? '● ONLİNE' : '○ OFFLİNE'}
          </span>
        )}
      </div>

      <div className="modem-tabs">
        {TABS.map(([t, label]) => (
          <button key={t} className={`modem-tab${tab === t ? ' active' : ''}`} onClick={() => setTab(t)}>
            {label}
          </button>
        ))}
      </div>

      <div className="modem-body">
        {/* ── STATUS ── */}
        {tab === 'status' && (
          <div className="modem-status">
            <div className="status-grid">
              <div className="status-card">
                <label>OPERATOR</label>
                <span>{status?.operator || '—'}</span>
              </div>
              <div className="status-card">
                <label>ŞƏBƏKƏ</label>
                <span>{status?.network_type || '—'}</span>
              </div>
              <div className="status-card">
                <label>SİQNAL</label>
                <span>{status ? <SignalBar percent={status.signal_percent} /> : '—'}</span>
              </div>
              <div className="status-card">
                <label>IP</label>
                <span>{status?.ip || '—'}</span>
              </div>
              <div className="status-card">
                <label>RX</label>
                <span className="rx">{status ? fmtBytes(status.rx_bytes) : '—'}</span>
              </div>
              <div className="status-card">
                <label>TX</label>
                <span className="tx">{status ? fmtBytes(status.tx_bytes) : '—'}</span>
              </div>
            </div>
            <div className="modem-actions">
              <button className="btn-connect" onClick={handleConnect} disabled={loading === 'connect'}>
                {loading === 'connect' ? '...' : '▶ BAĞLAN'}
              </button>
              <button className="btn-disconnect" onClick={handleDisconnect} disabled={loading === 'disconnect'}>
                {loading === 'disconnect' ? '...' : '■ KƏS'}
              </button>
              <button className="btn-refresh" onClick={fetchStatus} disabled={statusLoading}>
                {statusLoading ? '...' : '↻ YENİLƏ'}
              </button>
            </div>
          </div>
        )}

        {/* ── SMS ── */}
        {tab === 'sms' && (
          <div className="modem-sms">
            <div className="sms-send-form">
              <h3>SMS GÖNDƏR</h3>
              <div className="sms-inputs">
                <input
                  className="modem-input"
                  placeholder="Nömrə (+994...)"
                  value={smsTo}
                  onChange={e => setSmsTo(e.target.value)}
                />
                <textarea
                  className="modem-textarea"
                  placeholder="Mətn..."
                  rows={3}
                  value={smsBody}
                  onChange={e => setSmsBody(e.target.value)}
                />
                <button className="btn-connect" onClick={handleSendSms} disabled={loading === 'sms-send' || !smsTo || !smsBody}>
                  {loading === 'sms-send' ? '...' : '► GÖNDƏR'}
                </button>
              </div>
            </div>
            <div className="sms-list">
              <div className="sms-list-header">
                <h3>GƏLƏN SMS ({smsList.filter(s => s.status !== 'STO SENT').length})</h3>
                <button className="btn-refresh" onClick={fetchSms}>↻</button>
              </div>
              {smsList.length === 0 && <p className="empty-msg">SMS yoxdur</p>}
              {smsList.map(sms => (
                <div key={sms.index} className={`sms-item ${sms.status === 'REC UNREAD' ? 'unread' : ''}`}>
                  <div className="sms-meta">
                    <span className="sms-sender">{sms.sender || 'Naməlum'}</span>
                    <span className="sms-time">{sms.timestamp}</span>
                    <button className="sms-delete" onClick={() => handleDeleteSms(sms.index)}>✕</button>
                  </div>
                  <p className="sms-body">{sms.body}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── WIFI ── */}
        {tab === 'wifi' && (
          <div className="modem-wifi">
            {wifiStatus && (
              <div className="wifi-current">
                <span>Aktiv WiFi: </span>
                <strong>{wifiStatus.ssid || 'Yoxdur'}</strong>
                {wifiStatus.ip && <span className="wifi-ip"> — {wifiStatus.ip}</span>}
                {wifiStatus.connected && (
                  <button className="btn-disconnect btn-sm" onClick={handleWifiDisconnect}>Kəs</button>
                )}
              </div>
            )}
            <div className="wifi-list-header">
              <h3>ŞƏBƏKƏLƏR</h3>
              <button className="btn-refresh" onClick={fetchWifi} disabled={loading === 'wifi-scan'}>
                {loading === 'wifi-scan' ? '...' : '↻ SKAN'}
              </button>
            </div>
            {wifiNets.length === 0 && <p className="empty-msg">Şəbəkə tapılmadı</p>}
            {wifiNets.map(net => (
              <div key={net.ssid} className="wifi-item">
                <div className="wifi-info">
                  <span className="wifi-ssid">{net.ssid}</span>
                  <SignalBar percent={net.signal_percent} />
                  {net.encrypted && <span className="wifi-lock">🔒</span>}
                </div>
                <div className="wifi-connect-row">
                  {net.encrypted && (
                    <input
                      className="modem-input wifi-pass"
                      type="password"
                      placeholder="Şifrə"
                      value={wifiPassword[net.ssid] || ''}
                      onChange={e => setWifiPassword(p => ({ ...p, [net.ssid]: e.target.value }))}
                    />
                  )}
                  <button
                    className="btn-connect btn-sm"
                    onClick={() => handleWifiConnect(net.ssid)}
                    disabled={loading === `wifi-${net.ssid}`}
                  >
                    {loading === `wifi-${net.ssid}` ? '...' : 'QOŞUL'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── CONFIG ── */}
        {tab === 'config' && (
          <div className="modem-config">
            <h3>APN KONFİQURASİYASI</h3>
            <div className="config-form">
              <label>APN</label>
              <input className="modem-input" value={apn} onChange={e => setApn(e.target.value)} placeholder="internet" />
              <label>İstifadəçi (boş ola bilər)</label>
              <input className="modem-input" value={apnUser} onChange={e => setApnUser(e.target.value)} placeholder="" />
              <label>Şifrə (boş ola bilər)</label>
              <input className="modem-input" type="password" value={apnPass} onChange={e => setApnPass(e.target.value)} placeholder="" />
              <button className="btn-connect" onClick={handleSaveApn} disabled={loading === 'apn' || !apn}>
                {loading === 'apn' ? '...' : '✓ SAXLA'}
              </button>
            </div>
            <div className="config-hint">
              <p>Azercell: <code>internet</code></p>
              <p>Bakcell: <code>internet.bakcell.com</code></p>
              <p>Nar: <code>www.narinternet.az</code></p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
